<!-- src/pages/IngredientsView.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_ALL_INGREDIENTS, GET_INGREDIENT_GROUPS } from '../graphql/queries'
import { DELETE_INGREDIENT } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import IngredientModal from '../components/IngredientModal.vue'

const route = useRoute()

// State
const searchQuery = ref('')
const selectedGroup = ref('')
const sortBy = ref('name')
const sortOrder = ref('asc')
const viewMode = ref('grouped') // 'grouped' or 'list'
const showCreateModal = ref(false)
const showEditModal = ref(false)
const showDeleteDialog = ref(false)
const selectedIngredient = ref<any>(null)

// GraphQL queries
const { result: ingredientsResult, loading: loadingIngredients, refetch: refetchIngredients } = useQuery(GET_ALL_INGREDIENTS)
const { result: groupsResult, refetch: refetchGroups } = useQuery(GET_INGREDIENT_GROUPS)

// Mutations
const { mutate: deleteIngredientMutation, loading: deletingIngredient } = useMutation(DELETE_INGREDIENT)

// Computed properties
const ingredients = computed(() => ingredientsResult.value?.getAllIngredients || [])
const ingredientGroups = computed(() => groupsResult.value?.getAllIngredientGroups || [])

const filteredIngredients = computed(() => {
  let filtered = ingredients.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((ingredient: any) => 
      ingredient.name.toLowerCase().includes(query) ||
      ingredient.description?.toLowerCase().includes(query) ||
      ingredient.group?.name?.toLowerCase().includes(query)
    )
  }

  // Group filter
  if (selectedGroup.value) {
    filtered = filtered.filter((ingredient: any) => 
      ingredient.group?.id === selectedGroup.value
    )
  }

  // Sort (create a copy before sorting to avoid mutating the reactive array)
  return [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortBy.value]
    let bVal = b[sortBy.value]

    if (sortBy.value === 'group') {
      aVal = a.group?.name || ''
      bVal = b.group?.name || ''
    }

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }

    if (sortOrder.value === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
    }
  })
})

const groupedIngredients = computed(() => {
  const grouped: { [key: string]: any[] } = {}
  
  filteredIngredients.value.forEach((ingredient: any) => {
    const groupName = ingredient.group?.name || 'Ungrouped'
    if (!grouped[groupName]) {
      grouped[groupName] = []
    }
    grouped[groupName].push(ingredient)
  })

  // Sort groups alphabetically and sort ingredients within each group using user preferences
  const sortedGroups: { [key: string]: any[] } = {}
  Object.keys(grouped).sort().forEach(groupName => {
    sortedGroups[groupName] = [...grouped[groupName]].sort((a: any, b: any) => {
      let aVal = a[sortBy.value]
      let bVal = b[sortBy.value]

      if (sortBy.value === 'group') {
        aVal = a.group?.name || ''
        bVal = b.group?.name || ''
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (sortOrder.value === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })
  })

  return sortedGroups
})

const sortOptions = [
  { value: 'name', label: 'Name' },
  { value: 'group', label: 'Group' },
  { value: 'created_at', label: 'Date Created' }
]

// Methods
const deleteIngredient = (ingredient: any) => {
  selectedIngredient.value = ingredient
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!selectedIngredient.value) return
  
  try {
    await deleteIngredientMutation({ id: selectedIngredient.value.id })
    showDeleteDialog.value = false
    selectedIngredient.value = null
    showSuccess('Ingredient deleted successfully!')
    refetchIngredients()
  } catch (error) {
    showError(error, 'delete ingredient')
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
  selectedIngredient.value = null
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedGroup.value = ''
  sortBy.value = 'name'
  sortOrder.value = 'asc'
}

const editIngredient = (ingredient: any) => {
  selectedIngredient.value = ingredient
  showEditModal.value = true
}

const closeCreateModal = () => {
  showCreateModal.value = false
}

const closeEditModal = () => {
  showEditModal.value = false
  selectedIngredient.value = null
}

const onIngredientSubmitted = async () => {
  // Refetch both ingredients and groups to ensure cache is up to date
  await Promise.all([
    refetchIngredients(),
    refetchGroups()
  ])
}

// Watch for route changes
watch(() => route.query.action, (action) => {
  if (action === 'create') {
    showCreateModal.value = true
  }
})
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Ingredients</h1>
        <p class="text-gray-600 mt-1">Manage your ingredient catalog</p>
      </div>
      <div class="flex items-center gap-2">
        <!-- View Mode Toggle -->
        <div class="bg-gray-200 rounded-lg p-1 flex">
          <button
            @click="viewMode = 'grouped'"
            :class="`px-3 py-1 rounded text-sm transition-colors ${
              viewMode === 'grouped' 
                ? 'bg-white text-gray-900 shadow' 
                : 'text-gray-600 hover:text-gray-900'
            }`"
          >
            Grouped
          </button>
          <button
            @click="viewMode = 'list'"
            :class="`px-3 py-1 rounded text-sm transition-colors ${
              viewMode === 'list' 
                ? 'bg-white text-gray-900 shadow' 
                : 'text-gray-600 hover:text-gray-900'
            }`"
          >
            List
          </button>
        </div>
        <!-- Create Button -->
        <button 
          @click="showCreateModal = true"
          class="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
        >
          <span class="text-xl">+</span>
          Add Ingredient
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Search -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Search ingredients..."
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <!-- Group Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Group</label>
          <select 
            v-model="selectedGroup"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Groups</option>
            <option 
              v-for="group in ingredientGroups" 
              :key="group.id" 
              :value="group.id"
            >
              {{ group.name }}
            </option>
          </select>
        </div>

        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <select 
            v-model="sortBy"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option 
              v-for="option in sortOptions" 
              :key="option.value" 
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sort Order & Clear -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Sort Order</label>
          <div class="flex gap-2">
            <select 
              v-model="sortOrder"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="asc">A-Z</option>
              <option value="desc">Z-A</option>
            </select>
            <button 
              @click="clearFilters"
              class="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Results Summary -->
      <div class="mt-4 text-sm text-gray-600">
        Showing {{ filteredIngredients.length }} of {{ ingredients.length }} ingredient{{ ingredients.length !== 1 ? 's' : '' }}
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loadingIngredients" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      <p class="mt-4 text-gray-500">Loading ingredients...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredIngredients.length === 0" class="text-center py-12">
      <div class="text-6xl mb-4">🥕</div>
      <h3 class="text-xl font-semibold text-gray-700 mb-2">No ingredients found</h3>
      <p class="text-gray-500 mb-4">
        {{ ingredients.length === 0 
          ? 'Start building your ingredient catalog' 
          : 'Try adjusting your search or filters'
        }}
      </p>
      <div class="space-x-2">
        <button 
          v-if="searchQuery || selectedGroup"
          @click="clearFilters"
          class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Clear Filters
        </button>
        <button 
          @click="showCreateModal = true"
          class="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          Add First Ingredient
        </button>
      </div>
    </div>

    <!-- Grouped View -->
    <div v-else-if="viewMode === 'grouped'" class="space-y-6">
      <div 
        v-for="(groupIngredients, groupName) in groupedIngredients" 
        :key="groupName"
        class="bg-white rounded-lg shadow-md overflow-hidden"
      >
        <!-- Group Header -->
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900 flex items-center justify-between">
            <span class="flex items-center">
              <span class="mr-2">📦</span>
              {{ groupName }}
            </span>
            <span class="text-sm font-normal text-gray-500">
              {{ groupIngredients.length }} ingredient{{ groupIngredients.length !== 1 ? 's' : '' }}
            </span>
          </h2>
        </div>

        <!-- Group Items -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
          <div 
            v-for="ingredient in groupIngredients" 
            :key="ingredient.id"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex justify-between items-start mb-2">
              <h3 class="font-semibold text-gray-900">{{ ingredient.name }}</h3>
              <div class="flex space-x-1">
                <button 
                  @click="editIngredient(ingredient)"
                  class="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Edit ingredient"
                >
                  ✏️
                </button>
                <button 
                  @click="deleteIngredient(ingredient)"
                  class="p-1 text-red-600 hover:text-red-800 transition-colors"
                  title="Delete ingredient"
                >
                  🗑️
                </button>
              </div>
            </div>

            <p v-if="ingredient.description" class="text-sm text-gray-600 mb-3">
              {{ ingredient.description }}
            </p>

            <div class="space-y-1 text-xs text-gray-500">
              <div v-if="ingredient.default_unit">
                Default unit: {{ ingredient.default_unit }}
              </div>
              <div v-if="ingredient.calories_per_100g">
                Calories: {{ ingredient.calories_per_100g }}/100g
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr 
              v-for="ingredient in filteredIngredients" 
              :key="ingredient.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <!-- Name -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="font-medium text-gray-900">{{ ingredient.name }}</div>
              </td>

              <!-- Group -->
              <td class="px-6 py-4 whitespace-nowrap">
                <span v-if="ingredient.group.name" class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {{ ingredient.group.name }}
                </span>
                <span v-else class="text-gray-400 text-sm">No group</span>
              </td>

              <!-- Actions -->
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                <button 
                  @click="editIngredient(ingredient)"
                  class="text-blue-600 hover:text-blue-800 transition-colors"
                  title="Edit ingredient"
                >
                  ✏️
                </button>
                <button 
                  @click="deleteIngredient(ingredient)"
                  class="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete ingredient"
                >
                  🗑️
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Statistics -->
    <div v-if="ingredients.length > 0" class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-blue-600 mb-2">
          {{ ingredients.length }}
        </div>
        <div class="text-gray-600">Total Ingredients</div>
      </div>
      
      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-green-600 mb-2">
          {{ ingredientGroups.length }}
        </div>
        <div class="text-gray-600">Groups</div>
      </div>
      
      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-purple-600 mb-2">
          {{ ingredients.filter((ing: any) => ing.calories_per_100g).length }}
        </div>
        <div class="text-gray-600">With Nutrition Info</div>
      </div>
    </div>

    <!-- Create Ingredient Modal -->
    <IngredientModal
      :show="showCreateModal"
      @close="closeCreateModal"
      @submitted="onIngredientSubmitted"
    />

    <!-- Edit Ingredient Modal -->
    <IngredientModal
      :show="showEditModal"
      :ingredient="selectedIngredient"
      @close="closeEditModal"
      @submitted="onIngredientSubmitted"
    />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      :show="showDeleteDialog"
      title="Delete Ingredient"
      :message="selectedIngredient ? `Are you sure you want to delete &quot;${selectedIngredient.name}&quot;? This action cannot be undone.` : ''"
      confirm-text="Delete"
      :loading="deletingIngredient"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>
